import { Chart } from 'chart.js';

const euro = (cents) => '€ ' + (cents / 100).toLocaleString('nl-BE', { maximumFractionDigits: 0 });

/** PAX-8416 — line chart of weekly allotment snapshots (x = ISO week). */
export function allotmentProgressieChart(config) {
  const palette = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#be185d', '#4d7c0f', '#b45309', '#475569'];
  return {
    chart: null,
    observer: null,

    init() {
      this.$nextTick(() => this.render());
      this.observer = new MutationObserver(() => this.retheme());
      this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    },

    destroy() {
      this.observer?.disconnect();
      this.chart?.destroy();
    },

    retheme() {
      this.chart?.destroy();
      this.chart = null;
      this.render();
    },

    render() {
      if (!config.weken.length || !config.reeksen.length) return;
      const dark = document.documentElement.classList.contains('dark');
      const text = dark ? '#9ca3af' : '#6b7280';
      const grid = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
      const tooltip = dark ? '#1f2937' : '#111827';
      const suffix = config.metriek === 'bezetting' ? '%' : '';
      this.chart = new Chart(this.$refs.progressie, {
        type: 'line',
        data: {
          labels: config.weken,
          datasets: config.reeksen.map((reeks, index) => ({
            label: reeks.label,
            data: reeks.waarden,
            borderColor: palette[index % palette.length],
            backgroundColor: palette[index % palette.length],
            spanGaps: true,
            tension: 0.2,
            pointRadius: 3,
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { color: text, usePointStyle: true, padding: 14 } },
            tooltip: { backgroundColor: tooltip, callbacks: { label: ctx => ` ${ctx.dataset.label}: ${ctx.raw ?? '—'}${suffix}` } },
          },
          scales: {
            x: { ticks: { color: text }, grid: { display: false } },
            y: { beginAtZero: true, ticks: { color: text, callback: value => `${value}${suffix}` }, grid: { color: grid } },
          },
        },
      });
    },
  };
}

/** Chart.js component for the read-only allotment report. */
export function allotmentReportCharts(config) {
  return {
    charts: [],
    observer: null,

    init() {
      this.$nextTick(() => this.render());
      this.observer = new MutationObserver(() => this.retheme());
      this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    },

    destroy() {
      this.observer?.disconnect();
      this.charts.forEach(chart => chart.destroy());
    },

    retheme() {
      this.charts.forEach(chart => chart.destroy());
      this.charts = [];
      this.render();
    },

    render() {
      const dark = document.documentElement.classList.contains('dark');
      const text = dark ? '#9ca3af' : '#6b7280';
      const grid = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
      const tooltip = dark ? '#1f2937' : '#111827';
      if (config.kamerstatussen.length) {
        this.charts.push(new Chart(this.$refs.kamers, {
          type: 'doughnut',
          data: { labels: config.kamerstatussen.map(row => row.label), datasets: [{ data: config.kamerstatussen.map(row => row.waarde), backgroundColor: config.kamerstatussen.map(row => row.kleur), borderWidth: 0 }] },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { color: text, usePointStyle: true, padding: 14 } }, tooltip: { backgroundColor: tooltip, callbacks: { label: ctx => ` ${ctx.label}: ${ctx.raw} kamer${ctx.raw === 1 ? '' : 's'}` } } } },
        }));
      }
      if (config.prijsPerBand.length) {
        this.charts.push(new Chart(this.$refs.prijzen, {
          type: 'bar',
          data: { labels: config.prijsPerBand.map(row => row.label), datasets: [
            { label: 'Kost', data: config.prijsPerBand.map(row => row.kost_cents), backgroundColor: '#94a3b8', borderRadius: 4 },
            { label: 'Verkoopprijs', data: config.prijsPerBand.map(row => row.verkoop_cents), backgroundColor: '#2563eb', borderRadius: 4 },
          ] },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { labels: { color: text, usePointStyle: true } }, tooltip: { backgroundColor: tooltip, callbacks: { label: ctx => `${ctx.dataset.label}: ${euro(ctx.raw)}`, afterBody: items => { const margin = config.prijsPerBand[items[0].dataIndex]?.bw_pct; return margin === null ? '' : `Gemiddelde BW: ${(margin * 100).toLocaleString('nl-BE', { maximumFractionDigits: 1 })}%`; } } } }, scales: { x: { ticks: { color: text }, grid: { display: false } }, y: { beginAtZero: true, ticks: { color: text, callback: value => euro(value) }, grid: { color: grid } } } },
        }));
      }
    },
  };
}
